import { RevenueOptionsType } from 'src/integration/types/RevenueOptionsType';
import { SocAndBalancing } from 'src/integration/types/SocAndBalancing';
import useExportToCsv from 'src/toolkit/hooks/useExportToCsv';

interface ChartDataset {
  label: string;
  data: Array<number | { x: string; y: number }>;
}

interface StackedChartData {
  labels: string[];
  datasets: ChartDataset[];
}

interface RevenueItem {
  date: string;
  revenue: {
    amount: number;
  };
  revenueMw: {
    amount: number;
  };
}

interface CsvExportParams {
  selectedDate: string;
  selectedSiteId?: number | null;
  selectedRevenueOption: RevenueOptionsType;
  revenueData: RevenueItem[];
  socAndBalancing: SocAndBalancing[];
  ffrEnergyPriceChartData: StackedChartData;
  ffrCapacityPriceChartData: StackedChartData;
  energyTradedAndActivationsChartData: StackedChartData;
  capacityTradedAndActivationsChartData: StackedChartData;
}

const useNewDemoPageCsvExports = ({
  selectedDate,
  selectedSiteId,
  selectedRevenueOption,
  revenueData,
  socAndBalancing,
  ffrEnergyPriceChartData,
  ffrCapacityPriceChartData,
  energyTradedAndActivationsChartData,
  capacityTradedAndActivationsChartData,
}: CsvExportParams) => {
  const { exportToCsv } = useExportToCsv();
  const fileSuffix = `${selectedDate}-${selectedSiteId ?? 'all-sites'}`;

  const mapChartToRows = (chartData: StackedChartData) => {
    return chartData.labels.map((label, index) => {
      const row: Record<string, string | number> = {
        time: label,
      };

      chartData.datasets.forEach((dataset) => {
        const dataPoint = dataset.data[index];
        row[dataset.label] = typeof dataPoint === 'number' ? dataPoint : dataPoint?.y ?? '';
      });

      return row;
    });
  };

  const mapChartToHeaders = (chartData: StackedChartData) => {
    return [
      { key: 'time', label: 'Time' },
      ...chartData.datasets.map((dataset) => ({ key: dataset.label, label: dataset.label })),
    ];
  };

  const handleRevenueCsvExport = () => {
    const isRevenue = selectedRevenueOption === RevenueOptionsType.revenue;

    exportToCsv({
      fileName: `revenue-${fileSuffix}`,
      headers: [
        { key: 'date', label: 'Date' },
        { key: 'value', label: isRevenue ? 'Revenue' : 'Revenue / MW' },
      ],
      rows: revenueData.map((item) => ({
        date: item.date,
        value: isRevenue ? item.revenue.amount : item.revenueMw.amount,
      })),
    });
  };

  const handleSocTimeSeriesCsvExport = () => {
    exportToCsv({
      fileName: `soc-time-series-${fileSuffix}`,
      headers: [
        { key: 'date', label: 'Date' },
        { key: 'time', label: 'Time' },
        { key: 'soc', label: 'SOC' },
        { key: 'socUnit', label: 'SOC Unit' },
        { key: 'dayAheadPrice', label: 'Day ahead price' },
        { key: 'dayAheadPriceUnit', label: 'Day ahead price unit' },
        { key: 'isBidSuccessful', label: 'Bid successful' },
      ],
      rows: socAndBalancing.map((item) => ({
        date: item.date,
        time: item.time,
        soc: item.soc.amount,
        socUnit: item.soc.unit,
        dayAheadPrice: item.dayAheadPrice.amount,
        dayAheadPriceUnit: item.dayAheadPrice.currency,
        isBidSuccessful: item.isBidSuccessful,
      })),
    });
  };

  const handleBalancingEventsCsvExport = () => {
    exportToCsv({
      fileName: `balancing-events-${fileSuffix}`,
      headers: [
        { key: 'date', label: 'Date' },
        { key: 'time', label: 'Time' },
        { key: 'serviceType', label: 'Service type' },
        { key: 'energy', label: 'Energy' },
        { key: 'energyUnit', label: 'Energy unit' },
        { key: 'price', label: 'Price' },
        { key: 'priceUnit', label: 'Price unit' },
      ],
      rows: socAndBalancing.flatMap((item) => {
        if (!item.serviceEvent?.length) {
          return [];
        }

        return item.serviceEvent.map((serviceEvent) => ({
          date: item.date,
          time: item.time,
          serviceType: serviceEvent.serviceType,
          energy: serviceEvent.energy.amount,
          energyUnit: serviceEvent.energy.unit,
          price: serviceEvent.price.amount,
          priceUnit: serviceEvent.price.unit,
        }));
      }),
    });
  };

  const handleFfrEnergyPriceCsvExport = () => {
    exportToCsv({
      fileName: `ffr-energy-price-${fileSuffix}`,
      headers: mapChartToHeaders(ffrEnergyPriceChartData),
      rows: mapChartToRows(ffrEnergyPriceChartData),
    });
  };

  const handleFfrCapacityPriceCsvExport = () => {
    exportToCsv({
      fileName: `ffr-capacity-price-${fileSuffix}`,
      headers: mapChartToHeaders(ffrCapacityPriceChartData),
      rows: mapChartToRows(ffrCapacityPriceChartData),
    });
  };

  const handleFfrEnergyTradedAndActivationsCsvExport = () => {
    exportToCsv({
      fileName: `ffr-energy-traded-and-activations-${fileSuffix}`,
      headers: mapChartToHeaders(energyTradedAndActivationsChartData),
      rows: mapChartToRows(energyTradedAndActivationsChartData),
    });
  };

  const handleFfrCapacityTradedAndActivationsCsvExport = () => {
    exportToCsv({
      fileName: `ffr-capacity-traded-and-activations-${fileSuffix}`,
      headers: mapChartToHeaders(capacityTradedAndActivationsChartData),
      rows: mapChartToRows(capacityTradedAndActivationsChartData),
    });
  };

  return {
    handleRevenueCsvExport,
    handleSocTimeSeriesCsvExport,
    handleBalancingEventsCsvExport,
    handleFfrEnergyPriceCsvExport,
    handleFfrCapacityPriceCsvExport,
    handleFfrEnergyTradedAndActivationsCsvExport,
    handleFfrCapacityTradedAndActivationsCsvExport,
  };
};

export default useNewDemoPageCsvExports;